<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-pink-600 rounded-full"></div>
          <span class="text-[10px] font-black text-pink-600 tracking-widest">Recarga de Gas</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white flex items-center gap-2">
          <Icon icon="mdi:moped" class="text-pink-600" /> Delivery / Repartidores
          <span class="px-2 py-0.5 bg-pink-100 dark:bg-pink-900/40 text-pink-600 text-xs font-black rounded-lg">{{ deliveries.length }}</span>
        </h1>
      </div>
      <button @click="openCreateModal" class="px-4 py-2 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-xl font-black text-xs tracking-widest shadow-md flex items-center gap-2">
        <Icon icon="mdi:plus-circle" /> Nuevo Delivery
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-16">
      <Icon icon="mdi:loading" class="animate-spin text-pink-600" width="40" />
    </div>

    <!-- Empty -->
    <div v-else-if="deliveries.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border dark:border-gray-700 p-16 text-center">
      <Icon icon="mdi:account-off" class="text-6xl mb-3 mx-auto opacity-30" />
      <p class="text-lg font-black text-gray-400">No hay deliverys registrados</p>
    </div>

    <!-- Cards -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <div v-for="d in deliveries" :key="d.idDelivery" class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border dark:border-gray-700 overflow-hidden">
        <div :class="['px-4 py-3 flex items-center justify-between', d.disponible ? 'bg-gradient-to-r from-emerald-500 to-green-500' : 'bg-gradient-to-r from-gray-400 to-gray-500']">
          <div class="flex items-center gap-2">
            <Icon icon="mdi:account" class="text-white" />
            <span class="text-white font-black text-sm">{{ d.nombre }} {{ d.apellido }}</span>
          </div>
          <span class="px-2 py-0.5 bg-white/20 text-white text-[10px] font-black rounded">{{ d.disponible ? 'DISPONIBLE' : 'OCUPADO' }}</span>
        </div>
        <div class="p-4 space-y-3">
          <div class="text-xs space-y-1">
            <div class="flex items-center gap-2 text-gray-700 dark:text-gray-300"><Icon icon="mdi:card-account-details" class="text-gray-400" />{{ d.cedula }}</div>
            <div class="flex items-center gap-2 text-gray-700 dark:text-gray-300"><Icon icon="mdi:phone" class="text-gray-400" />{{ d.telefono }}</div>
            <div v-if="d.placaVehiculo" class="flex items-center gap-2 text-gray-700 dark:text-gray-300"><Icon icon="mdi:motorbike" class="text-gray-400" />{{ d.tipoVehiculo }} · {{ d.placaVehiculo }}</div>
            <div v-if="d.nombreZona" class="flex items-center gap-2 text-gray-700 dark:text-gray-300"><Icon icon="mdi:map-marker" class="text-gray-400" />{{ d.nombreZona }}</div>
            <div v-if="d.nombreUsuario" class="flex items-center gap-2 text-blue-600 dark:text-blue-400">
              <Icon icon="mdi:account-key" class="text-blue-500" />
              Usuario: {{ d.nombreUsuario }}
            </div>
            <div v-else class="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <Icon icon="mdi:alert-circle" class="text-amber-500" />
              Sin usuario vinculado
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2 bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <div class="text-center"><p class="text-[9px] font-black text-gray-400 uppercase">Entregas</p><p class="text-sm font-black text-pink-600">{{ d.totalEntregas || 0 }}</p></div>
            <div class="text-center"><p class="text-[9px] font-black text-gray-400 uppercase">⭐ Rating</p><p class="text-sm font-black text-amber-500">{{ d.calificacionPromedio || '-' }}</p></div>
          </div>
          <div class="flex gap-2 pt-2 border-t dark:border-gray-700">
            <button @click="toggleDisponible(d)" class="flex-1 py-2 rounded-lg text-[10px] font-black" :class="d.disponible ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'">
              {{ d.disponible ? 'Marcar ocupado' : 'Marcar disponible' }}
            </button>
            <button @click="openEditModal(d)" class="p-2 bg-amber-50 text-amber-600 rounded-lg"><Icon icon="mdi:pencil" /></button>
            <button @click="deleteDelivery(d.idDelivery)" class="p-2 bg-rose-50 text-rose-600 rounded-lg"><Icon icon="mdi:delete" /></button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="closeModal">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          <div class="bg-gradient-to-r from-pink-600 to-rose-600 px-6 py-4 flex items-center justify-between">
            <h3 class="text-sm font-black text-white">{{ editing ? 'Editar Delivery' : 'Nuevo Delivery' }}</h3>
            <button @click="closeModal" class="p-1 bg-white/10 rounded text-white"><Icon icon="mdi:close" /></button>
          </div>
          <div class="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <form @submit.prevent="save" class="space-y-4">

              <!-- ⭐ BUSCADOR DE USUARIO -->
              <div class="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-xl p-4">
                <label class="block text-[10px] font-black text-blue-700 dark:text-blue-300 tracking-widest mb-2">
                  <Icon icon="mdi:account-search" class="inline mr-1" />
                  Buscar Usuario del Sistema *
                </label>

                <!-- Input de búsqueda -->
                <div class="relative" ref="buscadorWrapper">
                  <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
                  <input
                    v-model="busquedaUsuario"
                    type="text"
                    :placeholder="usuarioSeleccionado ? `${usuarioSeleccionado.nombre} ${usuarioSeleccionado.apellido}` : 'Escribe nombre, apellido o cédula...'"
                    class="w-full pl-10 pr-10 py-3 bg-white dark:bg-gray-900/50 border-2 border-blue-300 focus:border-blue-500 rounded-xl text-xs font-bold dark:text-white outline-none"
                    @focus="mostrarDropdown = true"
                    @input="mostrarDropdown = true"
                  />
                  <button
                    v-if="usuarioSeleccionado"
                    type="button"
                    @click.stop="limpiarUsuario"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-rose-500"
                  >
                    <Icon icon="mdi:close-circle" class="text-lg" />
                  </button>
                </div>

                <!-- Dropdown de resultados -->
                <div v-if="mostrarDropdown" class="relative">
                  <div class="absolute z-50 top-1 left-0 right-0 mt-1 bg-white dark:bg-gray-800 border-2 border-blue-300 rounded-xl shadow-2xl max-h-64 overflow-y-auto">
                    <!-- Sin usuarios -->
                    <div v-if="usuariosFiltrados.length === 0" class="px-4 py-6 text-center">
                      <Icon icon="mdi:account-question" class="text-3xl text-gray-300 mx-auto mb-2" />
                      <p class="text-[11px] font-bold text-gray-400">
                        <span v-if="!busquedaUsuario">No hay usuarios con rol Delivery</span>
                        <span v-else>Sin resultados para "{{ busquedaUsuario }}"</span>
                      </p>
                    </div>

                    <!-- Lista de usuarios -->
                    <button
                      v-for="u in usuariosFiltrados"
                      :key="u.idUsuario"
                      type="button"
                      @click="seleccionarUsuario(u)"
                      class="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/30 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors flex items-center gap-3"
                    >
                      <div class="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-black text-xs flex-shrink-0">
                        {{ (u.nombre?.[0] || '?').toUpperCase() }}{{ (u.apellido?.[0] || '').toUpperCase() }}
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-xs font-black text-gray-900 dark:text-white truncate">
                          {{ u.nombre }} {{ u.apellido }}
                        </p>
                        <p class="text-[10px] text-gray-500 font-semibold truncate">
                          {{ u.nombreUsuario }}
                          <span v-if="u.cedula"> · {{ u.cedula }}</span>
                        </p>
                      </div>
                      <span class="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-[9px] font-black rounded uppercase flex-shrink-0">
                        {{ u.nombreRol }}
                      </span>
                    </button>
                  </div>
                </div>

                <!-- Usuario seleccionado (chip) -->
                <div v-if="usuarioSeleccionado" class="mt-3 flex items-center gap-3 bg-white dark:bg-gray-900/50 border-2 border-emerald-400 rounded-xl p-3">
                  <div class="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center text-white font-black text-sm flex-shrink-0">
                    {{ (usuarioSeleccionado.nombre?.[0] || '?').toUpperCase() }}{{ (usuarioSeleccionado.apellido?.[0] || '').toUpperCase() }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-xs font-black text-gray-900 dark:text-white truncate">
                      {{ usuarioSeleccionado.nombre }} {{ usuarioSeleccionado.apellido }}
                    </p>
                    <p class="text-[10px] text-gray-500 font-semibold truncate">
                      {{ usuarioSeleccionado.nombreUsuario }}
                      <span v-if="usuarioSeleccionado.cedula"> · {{ usuarioSeleccionado.cedula }}</span>
                    </p>
                  </div>
                  <Icon icon="mdi:check-circle" class="text-emerald-500 text-2xl flex-shrink-0" />
                </div>

                <p class="text-[10px] text-blue-600 dark:text-blue-400 font-bold mt-2 flex items-start gap-1">
                  <Icon icon="mdi:information-outline" class="text-sm flex-shrink-0 mt-0.5" />
                  <span>Busca y selecciona el usuario con el que el delivery iniciará sesión para ver sus asignaciones.</span>
                </p>
                <p v-if="usuarios.length === 0" class="text-[10px] text-rose-600 font-bold mt-2">
                  ⚠ No hay usuarios con rol Delivery. Crea uno primero en el módulo de Usuarios.
                </p>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div><label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Nombre *</label><input v-model="form.nombre" required class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" /></div>
                <div><label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Apellido *</label><input v-model="form.apellido" required class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" /></div>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div><label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Cédula *</label><input v-model="form.cedula" required class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" /></div>
                <div><label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Teléfono *</label><input v-model="form.telefono" required class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" /></div>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div><label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Email</label><input v-model="form.email" type="email" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" /></div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Zona</label>
                  <select v-model="form.idZona" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white">
                    <option :value="null">Sin zona</option>
                    <option v-for="z in zonas" :key="z.idZona" :value="z.idZona">{{ z.nombre }}</option>
                  </select>
                </div>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Tipo Vehículo</label>
                  <select v-model="form.tipoVehiculo" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white">
                    <option value="moto">Moto</option>
                    <option value="carro">Carro</option>
                    <option value="camioneta">Camioneta</option>
                    <option value="bicicleta">Bicicleta</option>
                  </select>
                </div>
                <div><label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Placa</label><input v-model="form.placaVehiculo" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" /></div>
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div><label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Comisión %</label><input v-model.number="form.comisionPorcentaje" type="number" step="0.01" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" /></div>
                <div><label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Comisión Fija $</label><input v-model.number="form.comisionFijaUsd" type="number" step="0.01" class="w-full px-4 py-2 border-2 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white" /></div>
              </div>
              <div class="flex gap-3 pt-4 border-t dark:border-gray-700">
                <button type="button" @click="closeModal" class="flex-1 py-3 border-2 rounded-xl font-black text-xs dark:text-gray-300">Cancelar</button>
                <button type="submit" :disabled="saving || !form.idUsuario" class="flex-[2] py-3 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50">
                  {{ saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import {
  obtenerDeliveries,
  crearDelivery,
  actualizarDelivery,
  eliminarDelivery,
  actualizarDisponibilidadDelivery,
  obtenerZonas
} from '@/api/gas.js';

import { obtenerUsuarios } from '@/api/usuarios.js';

export default {
  name: 'DeliveryList',
  components: { Icon },
  data() {
    return {
      deliveries: [],
      zonas: [],
      usuarios: [],
      loading: false,
      saving: false,
      showModal: false,
      editing: null,

      // ⭐ Búsqueda de usuario
      busquedaUsuario: '',
      usuarioSeleccionado: null,
      mostrarDropdown: false,

      form: {
        idUsuario: null,
        nombre: '',
        apellido: '',
        cedula: '',
        telefono: '',
        email: '',
        idZona: null,
        tipoVehiculo: 'moto',
        placaVehiculo: '',
        comisionPorcentaje: 0,
        comisionFijaUsd: 0,
        disponible: true,
        estado: 'activo'
      }
    };
  },
  computed: {
    // ⭐ Solo mostrar usuarios con rol Delivery
    usuariosDisponibles() {
      return this.usuarios.filter(u => {
        const rol = (u.nombreRol || u.rol || u.rolNombre || '').toLowerCase();
        return rol.includes('delivery') || rol.includes('repartidor');
      });
    },

    // ⭐ Filtrar por búsqueda
    usuariosFiltrados() {
      if (!this.busquedaUsuario || !this.busquedaUsuario.trim()) {
        return this.usuariosDisponibles;
      }
      const q = this.busquedaUsuario.toLowerCase().trim();
      return this.usuariosDisponibles.filter(u => {
        const nombre = (u.nombre || '').toLowerCase();
        const apellido = (u.apellido || '').toLowerCase();
        const usuario = (u.nombreUsuario || '').toLowerCase();
        const cedula = (u.cedula || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        return nombre.includes(q) || apellido.includes(q) || usuario.includes(q) || cedula.includes(q) || email.includes(q);
      });
    }
  },
  async mounted() {
    await this.load();
    await this.loadZonas();
    await this.loadUsuarios();

    // Cerrar dropdown al hacer clic fuera
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    // Cerrar dropdown cuando se hace clic fuera
    handleClickOutside(e) {
      const wrapper = this.$refs.buscadorWrapper;
      if (wrapper && !wrapper.contains(e.target)) {
        this.mostrarDropdown = false;
      }
    },

    // ⭐ Seleccionar usuario del dropdown
    seleccionarUsuario(u) {
      this.usuarioSeleccionado = u;
      this.form.idUsuario = u.idUsuario;
      this.busquedaUsuario = '';
      this.mostrarDropdown = false;
    },

    // ⭐ Limpiar selección
    limpiarUsuario() {
      this.usuarioSeleccionado = null;
      this.form.idUsuario = null;
      this.busquedaUsuario = '';
    },

    async load() {
      this.loading = true;
      try {
        const res = await obtenerDeliveries();
        if (res.success) this.deliveries = res.data;
      } finally {
        this.loading = false;
      }
    },

    async loadZonas() {
      const res = await obtenerZonas('activas');
      if (res.success) this.zonas = res.data;
    },

    async loadUsuarios() {
      try {
        const res = await obtenerUsuarios();
        if (res.success) {
          this.usuarios = Array.isArray(res.data) ? res.data : [];
          console.log('👥 Usuarios cargados:', this.usuarios.length);
          console.log('🛵 Usuarios con rol Delivery:', this.usuariosDisponibles.length);
        }
      } catch (e) {
        console.warn('No se pudieron cargar usuarios:', e);
      }
    },

    openCreateModal() {
      this.editing = null;
      this.usuarioSeleccionado = null;
      this.busquedaUsuario = '';
      this.mostrarDropdown = false;
      this.form = {
        idUsuario: null,
        nombre: '',
        apellido: '',
        cedula: '',
        telefono: '',
        email: '',
        idZona: null,
        tipoVehiculo: 'moto',
        placaVehiculo: '',
        comisionPorcentaje: 0,
        comisionFijaUsd: 0,
        disponible: true,
        estado: 'activo'
      };
      this.showModal = true;
    },

    openEditModal(d) {
      this.editing = d;
      this.busquedaUsuario = '';
      this.mostrarDropdown = false;

      // Buscar el usuario vinculado para mostrarlo en el chip
      if (d.idUsuario) {
        const u = this.usuarios.find(x => x.idUsuario === d.idUsuario);
        this.usuarioSeleccionado = u || null;
      } else {
        this.usuarioSeleccionado = null;
      }

      this.form = {
        idUsuario: d.idUsuario || null,
        nombre: d.nombre || '',
        apellido: d.apellido || '',
        cedula: d.cedula || '',
        telefono: d.telefono || '',
        email: d.email || '',
        idZona: d.idZona || null,
        tipoVehiculo: d.tipoVehiculo || 'moto',
        placaVehiculo: d.placaVehiculo || '',
        comisionPorcentaje: d.comisionPorcentaje || 0,
        comisionFijaUsd: d.comisionFijaUsd || 0,
        disponible: d.disponible ?? true,
        estado: d.estado || 'activo'
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.mostrarDropdown = false;
      this.busquedaUsuario = '';
    },

    async save() {
      if (!this.form.idUsuario) {
        alert('⚠ Debes seleccionar un usuario');
        return;
      }

      this.saving = true;
      try {
        const res = this.editing
          ? await actualizarDelivery(this.editing.idDelivery, this.form)
          : await crearDelivery(this.form);

        if (res.success) {
          await this.load();
          this.closeModal();
        } else {
          alert(res.error || 'Error');
        }
      } finally {
        this.saving = false;
      }
    },

    async deleteDelivery(id) {
      if (!confirm('¿Eliminar este delivery?')) return;
      const res = await eliminarDelivery(id);
      if (res.success) await this.load();
    },

    async toggleDisponible(d) {
      const res = await actualizarDisponibilidadDelivery(d.idDelivery, !d.disponible);
      if (res.success) await this.load();
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
</style>